"use client";

import { useEffect } from "react";

import {
  AD_OPTOUT_COOKIE,
  ANALYTICS_OPTOUT_COOKIE,
  GPC_APPLIED_COOKIE,
  isGpcSignalled,
  isPrivacyCookieUnset,
  writeAdOptOut,
  writeAnalyticsOptOut,
  writeGpcApplied,
} from "@/lib/privacyCookie";

/**
 * Client-side Global Privacy Control applier.
 *
 * `src/proxy.ts` already does this server-side off the `Sec-GPC: 1` header —
 * but a response served from the edge cache never reaches the origin, so the
 * proxy never runs and GPC would silently stop being honored for exactly the
 * visitors it protects. This is the same rule, executed in the browser, where
 * a cache HIT can't hide it.
 *
 * Both paths write the identical cookies under the identical guard, and both
 * are idempotent, so whichever runs first wins and the other no-ops.
 *
 * **The guard is "completely absent", not "not true".** Once a cookie exists —
 * including an explicit `"false"` the user chose in the dialog — we leave it
 * alone, so a GPC user can opt back in and have it stick. (The proxy had the
 * looser `!== "true"` guard once; it overwrote `"false"` on every request and
 * made opting back in impossible. Fixed 2026-05-22; don't reintroduce it here.)
 */
export function GpcApplier() {
  useEffect(() => {
    if (!isGpcSignalled()) return;

    if (isPrivacyCookieUnset(AD_OPTOUT_COOKIE)) writeAdOptOut(true);
    if (isPrivacyCookieUnset(ANALYTICS_OPTOUT_COOKIE)) writeAnalyticsOptOut(true);
    // Sticky once written: the dialog's ", User Overridden" suffix covers the
    // case where the user later flips a category back on.
    if (isPrivacyCookieUnset(GPC_APPLIED_COOKIE)) writeGpcApplied(true);
  }, []);

  return null;
}

export default GpcApplier;
